import { Button, Empty, Table, TableProps } from 'antd';
import { useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onDeleteAdmin, onGetAdmins } from 'modules/admins/services/admins';

// models
import { Role } from 'core/models/user';
import { Admin } from 'modules/admins/models/Admin';

// components
import Icon from 'utils/icon';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import AuthActionComponent from 'components/authAction/authAction';

// styles
import styles from './list.module.scss';
import NoData from 'components/noData/noData';
import { useTranslation } from 'react-i18next';

function ListAdminsPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [admins, setAdmins] = useState<Admin[]>([]);
	const [tempDeletedAdmin, setTempDeletedAdmin] = useState<number>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps<Admin>['columns'] = [
		{
			title: t('ADMINS.AUTHOR'),
			dataIndex: 'name',
			key: 'name',
			render: (name, admin) => (
				<div className={styles.nameNumber}>
					<p>{name}</p>
					<span>{admin.email}</span>
				</div>
			),
		},
		{
			title: t('ADMINS.ROLES'),
			dataIndex: 'roles',
			key: 'roles',
			className: 'wrap',
			render: (roles) =>
				roles.map((role: Role, i: number) => (
					<span className="status fw-medium me-1 mb-1 d-inline-block" key={i}>
						{role.name}
					</span>
				)),
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (_, admin) => (
				<span className={`status fw-medium ${admin.active ? 'active' : 'inactive'}`}>{admin.active ? 'Active' : 'Inactive'}</span>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, admin) =>
				admin.name !== 'Super Admin' && (
					<div className={styles.actionsDiv}>
						<AuthActionComponent action="admin_delete">
							<Button type="link" style={{ padding: '0px' }} onClick={() => onShowConfirmation(admin.id)}>
								<Icon name="delete" width={35} height={35} />
							</Button>
						</AuthActionComponent>
						<AuthActionComponent action="admin_update">
							<Button type="link" style={{ padding: '0px' }}>
								<Link to={`${admin.id}`}>
									<Icon name="edit" width={35} height={35} />
								</Link>
							</Button>
							<Button type="link" style={{ padding: '0px' }}>
								<Link to={`${admin.id}/change-password`}>
									<Icon name="key" width={35} height={35} />
								</Link>
							</Button>
						</AuthActionComponent>
					</div>
				),
		},
	];

	useEffect(() => {
		getAdmins();
	}, []);

	const getAdmins = async () => {
		try {
			setIsLoading(true);

			const response = await onGetAdmins();
			setAdmins(response.data.data.admins);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetAdmins(page);
			setAdmins(response.data.data.admins);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			setIsLoadMore(false);
		}
	};

	const onShowConfirmation = (roleId: number) => {
		setShowConfirmation(true);
		setTempDeletedAdmin(roleId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteAdmin(tempDeletedAdmin as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('ADMINS.ADMIN_DELETED'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getAdmins();
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.OPS'), t('ADMINS.ADMIN_NOT_DELETED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('ADMINS.ADMINS_MANAGEMENT')}</h3>
				{/* <p>The most important info highlights</p> */}
			</div>
			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<div className={`sectionContainer listData`}>
						<header>
							<div>
								<h5>{totalRecords} {t('ADMINS.ADMINS')}</h5>
								<p>{t('ADMINS.INVITE_ADMINS_AND_MANAGE_ACCESS')}</p>
							</div>
							<AuthActionComponent action="admin_create">
								<Link to="create">
									<Button type="primary" icon={<PlusOutlined />} style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }}>
										{t('ADMINS.INVITE_ADMINS')}
									</Button>
								</Link>
							</AuthActionComponent>
						</header>
						<div style={{ position: 'relative' }}>
							<Table
								columns={columns}
								dataSource={admins}
								scroll={{ x: 600 }}
								rowKey="id"
								pagination={{
									pageSize: 15,
									total: totalRecords,
									showSizeChanger: false,
									current: currentPage,
									onChange: fetchMoreData,
								}}
								locale={{ emptyText: <NoData /> }}
							/>
							{isLoadMore && (
								<div className="loadMoreContainer">
									<LoadingComponent />
								</div>
							)}
						</div>
					</div>
				</>
			)}

			<ConfirmModalComponent
				open={showConfirmation}
				title={t('ADMINS.DELETE_ADMIN')}
				confirmText={t('COMMON.DELETE')}
				description={t('ADMINS.DELETE_MODAL_DESCRIPTION')}
				note={t('ADMINS.DELETE_MODAL_NOTE')}
				onDiscard={onDiscardConfirmation}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default ListAdminsPage;
